import { AsyncPipe, SlicePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ChangeDetectorRef,
  output,
  inject,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { CouponService } from '@fitness-force/api';
import { ErrorMsgComponent } from '@fitness-force/errors';
import type { UpdateCouponLinkRequest } from '@fitness-force/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastMessageService } from '@fitness-force/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InplaceModule } from 'primeng/inplace';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ScrollPanelModule } from 'primeng/scrollpanel';

import { BehaviorSubject, forkJoin, Observable } from 'rxjs';

@Component({
  selector: 'fitness-force-coupon-name-edit',
  templateUrl: './coupon-name-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    DialogModule,
    FormsModule,
    ReactiveFormsModule,
    ScrollPanelModule,
    ButtonModule,
    InputNumberModule,
    InputTextModule,
    ErrorMsgComponent,
    InplaceModule,
    TranslateModule,
    AsyncPipe,
    SlicePipe,
  ],
})
export class CouponNameEditComponent implements OnInit {
  private formBuilder = inject(UntypedFormBuilder);
  private coupnService = inject(CouponService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private toastMessageService = inject(ToastMessageService);
  translate = inject(TranslateService);

  additionData: Observable<any> = new BehaviorSubject(null);
  columnData: Observable<any> = new BehaviorSubject(null);
  cData: any;
  aData: any;

  edit = new BehaviorSubject(false);
  inplaceForm!: UntypedFormGroup;
  readonly onAction = output<{
    action: string;
    columnData?: any;
    error?: any;
  }>();
  categoryForm: any;
  value!: string;
  isFormSubmitting = false;

  ngOnInit(): void {
    forkJoin([this.additionData, this.columnData]).subscribe({
      next: (data: any) => {
        this.aData = data[0];
        this.cData = data[1];
        this.value = this.cData.coupon_code;
        this.buildForm();
      },
    });
  }

  buildForm() {
    this.inplaceForm = this.formBuilder.group({
      value: [this.cData.coupon_code, [Validators.required]],
    });
  }

  updateValue() {
    if (this.inplaceForm.valid) {
      let rawValue = this.inplaceForm.getRawValue();
      this.isFormSubmitting = true;
      if (rawValue.value != this.cData.coupon_code) {
        let coupon_code = {
          coupon_code: rawValue.value,
          max_redepmtion_count: this.cData.coupon_max_redepmtion_count,
        };
        let payload: UpdateCouponLinkRequest = {} as UpdateCouponLinkRequest;
        payload.coupon_code = coupon_code;
        this.coupnService
          .UpdateCoupenMasterCouponLink(
            this.cData.tenant_id,
            this.cData.offer_master_id,
            this.cData.id,
            payload,
          )
          .subscribe({
            next: (res: any) => {
              this.isFormSubmitting = false;
              this.translate
                .get('ADMIN.COUPON.ERRORS.NAME_UPDTED')
                .subscribe((translation) => {
                  this.toastMessageService.showSuccess(translation);
                });
              this.columnData.subscribe({
                next: (data: any) => {
                  this.onAction.emit({
                    action: 'nameUpdated',
                    columnData: data,
                  });
                },
              });
              this.edit.next(false);
            },
            error: (error: any) => {
              this.isFormSubmitting = false;
              this.inplaceForm.controls.value.setValue(this.cData.coupon_code);
              this.edit.next(false);
              this.handleToastError(error);
              this.onAction.emit({ action: 'nameUpdatedError', error });
            },
          });
      } else {
        this.isFormSubmitting = false;
      }
    } else {
      this.edit.next(false);
    }
  }

  handleToastError(error: any) {
    this.toastMessageService.showError(error);
  }

  close() {
    this.edit.next(false);
    this.buildForm();
  }

  onKeyEnter(event: any) {
    this.updateValue();
  }
  onKeyEscape(event: any) {
    this.close();
  }
}
